import { GeolocationService } from "../services/geolocation-service";
import { FieldType, DataLayerErrorType } from "../types/enums";
import { ValidationDOMHelper } from "./validation-dom-helper";
import { ValidationRules } from "./validation-rules";
import { DataLayerService } from '../services/data-layer-service';
import { LocalizationService } from "../services/localization-service";
import { IValidationField } from "./validation-field.interface";

export class InputValidator {

/**
 * Validates multiple fields at once
 * @param fields Array of validation field configurations
 * @param needValidate Whether validation is needed
 * @param showErrorSpan Whether to show error messages
 * @param localizationService Service for getting localized error messages
 * @param dataLayerService Service for tracking validation errors to analytics
 * @returns true if ALL fields are valid, false if ANY field is invalid
 */
    public static validateFields(
        fields: IValidationField[],
        needValidate?: boolean,
        showErrorSpan?: boolean,
        localizationService?: LocalizationService,
        dataLayerService?: DataLayerService
    ): boolean {
        const results = fields.map(field =>
            InputValidator.validate(
                field.type,
                field.inputId,
                field.errorId,
                needValidate,
                showErrorSpan,
                field.extraData,
                field.extraIds,
                localizationService,
                dataLayerService
            )
        );

        return !results.some(result => result);
    }
    /**
     * Gets the localized error message for a validation error span.
     * @param errorSpanId - The ID of the error message element
     * @param localizationService - The localization service instance
     * @returns The localized error message
     */
    private static getLocalizedErrorMessage(
        errorSpanId: string, 
        localizationService: LocalizationService
    ): string {
        const errorElement = document.getElementById(errorSpanId);
        const ltagKey = errorElement?.getAttribute("ltag") ?? "";
        return localizationService.names(ltagKey);
    }

    /**
     * Normalizes error message
     * - Convert to lowercase
     * - Replace spaces with underscores  
     * - Remove trailing period
     * 
     * @example
     * "Invalid email address." → "invalid_email_address"
     * "Password must contain at least one number." → "password_must_contain_at_least_one_number"
     */
    private static normalizeErrorMessage(message: string): string {
        return message
            .trim()
            .toLowerCase()
            .replace(/\.$/, '')           // Remove trailing period
            .replace(/\s+/g, '_');        // Replace spaces with underscores
    }

    /**
     * Tracks a validation error to analytics with localized and normalized error message.
     * Can be called both internally within InputValidator methods or externally from components.
     * 
     * @param errorSpanId - The ID of the error message element (must have ltag attribute)
     * @param localizationService - Service for getting localized error messages
     * @param dataLayerService - Service for tracking validation errors to analytics
     */
    public static trackValidationError(
        errorSpanId: string,
        localizationService: LocalizationService,
        dataLayerService: DataLayerService
    ): void {
        const localizedError = this.getLocalizedErrorMessage(errorSpanId, localizationService);
        const normalizedError = this.normalizeErrorMessage(localizedError);
        dataLayerService.error_message(normalizedError, DataLayerErrorType.USER_ERROR);
    }

    public static trackValidationErrorByLtag(
        ltag: string,
        localizationService: LocalizationService,
        dataLayerService: DataLayerService
    ): void {
        const localizedError = localizationService.names(ltag);
        const normalizedError = this.normalizeErrorMessage(localizedError);
        dataLayerService.error_message(normalizedError, DataLayerErrorType.USER_ERROR);
    }


    public static validate(
        type: FieldType,
        inputRef: string | HTMLInputElement,
        errorSpanId: string,
        needValidate = true,
        showErrorSpan = true,
        extraData?: any[],
        extraIds?: string[],
        localizationService?: LocalizationService,
        dataLayerService?: DataLayerService
    ): boolean {
        if (!needValidate)
            return true;

        const input = typeof inputRef === "string"
            ? (document.getElementById(inputRef) as HTMLInputElement)
            : inputRef;

        if (!input)
            return false;

        let stringValue = "";
        if (input.type === "checkbox")
            stringValue = input.checked ? "true" : "false";
        else
            stringValue = input.value.trim();

        const isValid = ValidationRules.validate(type, stringValue, extraData);

        this._handleDomRendering(errorSpanId, showErrorSpan, isValid, input, extraIds);

        if (!isValid && dataLayerService && localizationService) {
            this.trackValidationError(errorSpanId, localizationService, dataLayerService);
        }

        return !isValid;
    }

    public static async validateAsync(
        geoService: GeolocationService,
        type: FieldType,
        inputRef: string | HTMLInputElement,
        errorSpanId: string,
        needValidate = true,
        showErrorSpan = true,
        localizationService?: LocalizationService,
        dataLayerService?: DataLayerService
    ): Promise<boolean> {
        if (!needValidate)
            return true;

        const input = typeof inputRef === "string"
            ? (document.getElementById(inputRef) as HTMLInputElement)
            : inputRef;

        if (!input)
            return false;

        const stringValue = input.value.trim();
        const isValid = await ValidationRules.validateAsync(type, stringValue, geoService);

        this._handleDomRendering(errorSpanId, showErrorSpan, isValid, input);

        if (!isValid && dataLayerService && localizationService) {
            this.trackValidationError(errorSpanId, localizationService, dataLayerService);
        }

        return !isValid;
    }

    private static _handleDomRendering(
        errorSpanId: string, 
        showErrorSpan: boolean, 
        isValid: boolean, 
        input: HTMLInputElement, 
        extraIds?: string[]
    ): void {
        if (!errorSpanId)
            return;

        if (!isValid && showErrorSpan)
            ValidationDOMHelper.applyErrorState(input, errorSpanId, extraIds);
        else
            ValidationDOMHelper.clearErrorState(input, errorSpanId, extraIds);
    }
}